import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useWebStore } from '../../context/WebStoreContext';
import { getArticleImageSlides } from '../../utils/imageHelper';

const ProductThumbnail = ({ product, size = 36 }) => {
  const candidateUrls = useMemo(() => {
    if (!product) return [];
    const slides = getArticleImageSlides(product);
    if (slides.length > 0 && slides[0].candidates && slides[0].candidates.length > 0) {
      return slides[0].candidates;
    }
    const raw = product?.d_pathimg || product?.imagen || product?.foto || product?.d_imagen;
    if (raw && typeof raw === 'string') {
      return [raw];
    }
    return [];
  }, [product]);

  const [candidateIdx, setCandidateIdx] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setCandidateIdx(0);
    setHasError(false);
  }, [product]);

  const currentUrl = candidateUrls[candidateIdx] || null;

  if (hasError || !currentUrl) {
    return (
      <div
        className="bg-light rounded-2 border d-flex align-items-center justify-content-center text-muted"
        style={{ width: `${size}px`, height: `${size}px`, flexShrink: 0 }}
      >
        <i className="bi bi-box-seam" style={{ fontSize: `${Math.round(size * 0.45)}px` }}></i>
      </div>
    );
  }

  return (
    <img
      src={currentUrl}
      alt={product.d_artic || 'Artículo'}
      className="rounded-2 border object-fit-contain bg-white"
      style={{ width: `${size}px`, height: `${size}px`, flexShrink: 0 }}
      onError={() => {
        if (candidateIdx + 1 < candidateUrls.length) {
          setCandidateIdx(prev => prev + 1);
        } else {
          setHasError(true);
        }
      }}
    />
  );
};

const getInitialConfig = (companyId = 1, companyName = 'HIPERPEL S.A.') => ({
  company_id: companyId,
  logoUrl: '/hiperpel-logo.png',
  logoText: companyName,
  footer: {
    companyName: companyName,
    tagline: 'Fabricantes de productos de cartón, envases, embalajes, descartables, gastronomía y librería. Venta mayorista y minorista con envíos a todo el país.',
    phone: '(011) 4247-6622',
    whatsapp: '5491142476622',
    email: 'ventas@hiperpel.com.ar',
    address: 'Vicente Damonte 1237 / 1247, Lanús Este (B1824ALG), Buenos Aires',
    scheduleMonFri: '08:00 a 18:00 hs',
    scheduleSat: '08:00 a 13:00 hs',
    websiteUrl: 'https://hiperpel.ar',
    instagram: 'https://instagram.com/hiperpel',
    facebook: 'https://facebook.com/hiperpel',
    twitter: '',
    linkedin: '',
    pinterest: '',
    copyrightText: `© ${new Date().getFullYear()} ${companyName} - Todos los derechos reservados.`
  },
  whatsappFloating: {
    enabled: true,
    phone: '5491142476622',
    message: `¡Hola! Me comunico desde el catálogo online de ${companyName} para hacer una consulta.`,
    pillText: '¿Dudas? ¡Escribinos!',
    showPill: true
  },
  commercial: {
    publicPricesEnabled: false,
    defaultPriceList: 1,
    saveOrderAsQuote: false,
    whatsappOrderNumber: '5491142476622',
    orderMessageTemplate: '¡Hola! Quiero confirmar mi pedido armado en la tienda online:'
  },
  modules: {
    filterBrands: true,
    filterColors: true,
    filterSizes: true,
    filterPresentation: true,
    filterOffers: true,
    filterStock: true,
    filterSort: true
  },
  relatedProducts: {
    enabled: true,
    title: 'Artículos Relacionados',
    subtitle: 'Productos similares que también te pueden interesar',
    criteria: 'subfamily_or_rubro', // 'subfamily_or_rubro' | 'same_rubro' | 'same_subfamily' | 'custom'
    maxItems: 6,
    customArticles: []
  }
});

const EcommerceAdminSettings = ({ user }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    () => user?.empresa_activa?.c_nuges || user?.empresa_activa?.id || 1
  );
  const { updateStoreConfig, refreshConfigs } = useWebStore();
  const [activeTab, setActiveTab] = useState('modules');
  const [config, setConfig] = useState(() => getInitialConfig(selectedCompanyId));
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Búsqueda y gestión de artículos relacionados personalizados
  const [searchArticuloQuery, setSearchArticuloQuery] = useState('');
  const [searchingArticulos, setSearchingArticulos] = useState(false);
  const [searchedArticulos, setSearchedArticulos] = useState([]);

  // 1. Cargar únicamente las empresas permitidas para el usuario autenticado
  useEffect(() => {
    if (Array.isArray(user?.empresas) && user.empresas.length > 0) {
      setCompanies(user.empresas);
    } else {
      const fetchCompanies = async () => {
        try {
          const res = await axios.get('/api/empresas/');
          if (Array.isArray(res.data) && res.data.length > 0) {
            setCompanies(res.data);
          }
        } catch (err) {
          console.warn('Error cargando empresas:', err);
        }
      };
      fetchCompanies();
    }
  }, [user]);

  // Obtener objeto y nombre de la empresa seleccionada para la tienda
  const activeCompany = useMemo(() => {
    const found = companies.find(c => String(c.c_nuges || c.id) === String(selectedCompanyId)) || {
      c_nuges: selectedCompanyId,
      id: selectedCompanyId,
      nombre: user?.empresa_activa?.nombre || 'HIPERPEL S.A.'
    };
    const rawDomain = (found?.d_sitien || '').replace(/www\.hiperpel\.com\.ar/gi, 'hiperpel.ar').replace(/hiperpel\.com\.ar/gi, 'hiperpel.ar');
    return { ...found, d_sitien: rawDomain };
  }, [companies, selectedCompanyId, user]);

  const activeCompanyName = activeCompany.nombre || user?.empresa_activa?.nombre || 'HIPERPEL S.A.';
  const currentCompanyId = selectedCompanyId;
  const currentCompanyName = activeCompanyName;

  // 2. Al iniciar, cargar la configuración actual de la tienda
  useEffect(() => {
    const loadInitialStoreConfig = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/api/ecommerce/config/', {
          params: { _t: Date.now() }
        });
        if (res.data && typeof res.data === 'object') {
          const compId = res.data.company_id ? Number(res.data.company_id) : selectedCompanyId;
          setSelectedCompanyId(compId);
          setConfig(prev => ({
            ...getInitialConfig(compId, activeCompanyName),
            ...res.data,
            company_id: compId
          }));
          fetchPriceLists(compId);
        } else {
          fetchPriceLists(selectedCompanyId);
        }
      } catch (err) {
        console.warn('Error cargando config inicial de tienda:', err);
        fetchPriceLists(selectedCompanyId);
      } finally {
        setLoading(false);
      }
    };
    loadInitialStoreConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPriceLists = async (companyId = selectedCompanyId) => {
    try {
      const res = await axios.get('/api/listas/', {
        params: { company: companyId, _t: Date.now() },
        headers: { 'X-Company-ID': String(companyId) }
      });
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      const validLists = data.filter(l => parseInt(l.c_situa, 10) !== 9);
      setPriceLists(validLists);
      if (validLists.length > 0) {
        setConfig(prev => {
          const currentList = prev.commercial?.defaultPriceList;
          const exists = validLists.some(l => Number(l.n_lista) === Number(currentList));
          if (!exists) {
            const defaultPl = validLists.find(l => l.l_prede) || validLists[0];
            return {
              ...prev,
              commercial: {
                ...(prev.commercial || {}),
                defaultPriceList: Number(defaultPl.n_lista)
              }
            };
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Error cargando listas de precios:', err);
    }
  };

  // Cambio de empresa en la configuración de tienda
  const handleCompanyChange = (newCompanyId) => {
    const compIdNum = Number(newCompanyId);
    setSelectedCompanyId(compIdNum);
    setConfig(prev => ({
      ...prev,
      company_id: compIdNum
    }));
    fetchPriceLists(compIdNum);
  };

  // Sincronizar automáticamente con la empresa activa del operador
  useEffect(() => {
    const activeId = user?.empresa_activa?.c_nuges || user?.empresa_activa?.id;
    if (activeId && Number(activeId) !== Number(selectedCompanyId)) {
      handleCompanyChange(Number(activeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.empresa_activa]);

  const handleModuleToggle = (key, value) => {
    setConfig(prev => ({
      ...prev,
      modules: {
        ...(prev.modules || {}),
        [key]: value
      }
    }));
  };

  // Guardar configuración para la empresa seleccionada en la tienda
  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const compIdNum = Number(selectedCompanyId);
      const { banners, heroBanners, carouselSettings, ...cleanConfig } = config;
      const payload = {
        ...cleanConfig,
        company_id: compIdNum
      };
      const res = await axios.post('/api/ecommerce/config/', payload, {
        params: { company: compIdNum, _t: Date.now() },
        headers: { 'X-Company-ID': String(compIdNum) }
      });
      if (updateStoreConfig) {
        updateStoreConfig(payload);
      }
      if (refreshConfigs) {
        refreshConfigs(compIdNum);
      }
      Swal.fire({
        icon: 'success',
        title: '¡Cambios Guardados!',
        text: res.data?.mensaje || `La tienda ahora quedó configurada con la empresa ${activeCompanyName}.`,
        confirmButtonColor: '#0d6efd',
        timer: 2500
      });
    } catch (err) {
      console.error('Error al guardar configuración:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error al Guardar',
        text: err.response?.data?.error || 'No se pudo guardar la configuración. Intentá nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setSaving(false);
    }
  };

  // Modificar configuración comercial
  const handleCommercialChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      commercial: {
        ...prev.commercial,
        [field]: value
      }
    }));
  };

  // Modificar configuración de artículos relacionados
  const handleRelatedChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      relatedProducts: {
        ...(prev.relatedProducts || {}),
        [field]: value
      }
    }));
  };

  // Búsqueda en catálogo de artículos para recomendación manual
  const handleSearchArticulos = async (query) => {
    setSearchArticuloQuery(query);
    if (!query || query.trim().length < 2) {
      setSearchedArticulos([]);
      return;
    }
    setSearchingArticulos(true);
    try {
      const res = await axios.get('/api/articulos/', {
        params: { search: query.trim(), page: 1, page_size: 10, company: selectedCompanyId },
        headers: { 'X-Company-ID': String(selectedCompanyId) }
      });
      const items = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setSearchedArticulos(items);
    } catch (err) {
      console.warn('Error buscando artículos:', err);
    } finally {
      setSearchingArticulos(false);
    }
  };

  // Agregar artículo a la lista de recomendados personalizados
  const handleAddCustomArticle = (art) => {
    const currentList = config.relatedProducts?.customArticles || [];
    const alreadyExists = currentList.some(item => (item.c_artic || item) === art.c_artic);
    if (alreadyExists) {
      Swal.fire({
        icon: 'info',
        title: 'Artículo ya agregado',
        text: 'Este artículo ya se encuentra en la lista de relacionados personalizados.',
        timer: 1800,
        showConfirmButton: false
      });
      return;
    }
    const cleanArt = {
      c_artic: art.c_artic,
      d_artic: art.d_artic,
      c_rubro: art.c_rubro,
      d_rubro: art.d_rubro,
      c_famil: art.c_famil,
      d_famil: art.d_famil,
      i_lista: art.i_lista,
      i_preant: art.i_preant,
      d_pathimg: art.d_pathimg || art.imagen || ''
    };
    handleRelatedChange('customArticles', [...currentList, cleanArt]);
    setSearchArticuloQuery('');
    setSearchedArticulos([]);
  };

  // Quitar artículo de la lista de recomendados personalizados
  const handleRemoveCustomArticle = (cArtic) => {
    const currentList = config.relatedProducts?.customArticles || [];
    const updated = currentList.filter(item => (item.c_artic || item) !== cArtic);
    handleRelatedChange('customArticles', updated);
  };

  // Restablecer valores de fábrica
  const handleResetDefaults = () => {
    Swal.fire({
      title: '¿Restablecer valores por defecto?',
      text: 'Se cargarán los colores, logos y datos iniciales de fábrica.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, restablecer',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        const resetData = getInitialConfig(currentCompanyId, currentCompanyName);
        setConfig(resetData);
        Swal.fire('Restablecido', 'Se aplicó la configuración inicial de la empresa.', 'info');
      }
    });
  };

  // Cambio de pestaña con auto-scroll / auto-enfoque centrado
  const handleTabSelect = (tabKey, e) => {
    setActiveTab(tabKey);
    if (e && e.currentTarget) {
      e.currentTarget.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5">
        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Cargando...</span>
        </div>
        <h5 className="text-secondary fw-semibold">Cargando configuración de la tienda...</h5>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 px-lg-4 py-4 position-relative pb-5">
      {/* 1. Header & Quick Actions Bar */}
      <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
        <div className="card-body p-4 bg-white">
          <div className="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-between gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                <span className="badge bg-primary-subtle text-primary fw-bold text-uppercase px-3 py-1 rounded-pill">
                  <i className="bi bi-shield-lock-fill me-1"></i> Administración SISGECOM
                </span>
                <span className="badge bg-secondary-subtle text-secondary rounded-pill">
                  Módulo Tienda Online
                </span>
                
                {/* Info de Empresa y Estado Web en SGES000 */}
                <div className="d-inline-flex align-items-center bg-light border border-primary-subtle rounded-pill px-3 py-1 shadow-sm gap-2 flex-wrap">
                  <i className="bi bi-building text-primary"></i>
                  <span className="fw-bold small text-dark">{activeCompanyName} (#{selectedCompanyId})</span>
                  {activeCompany?.l_sitien ? (
                    <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill small">
                      <i className="bi bi-check-circle-fill me-1"></i>Web Habilitada
                    </span>
                  ) : (
                    <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle rounded-pill small">
                      <i className="bi bi-exclamation-triangle-fill me-1"></i>Web Deshabilitada (l_sitien=No)
                    </span>
                  )}
                  {activeCompany?.d_sitien && (
                    <a
                      href={activeCompany.d_sitien.startsWith('http') ? activeCompany.d_sitien : `https://${activeCompany.d_sitien}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="small text-primary text-decoration-underline fw-semibold"
                    >
                      {activeCompany.d_sitien} <i className="bi bi-box-arrow-up-right"></i>
                    </a>
                  )}
                </div>
              </div>
              <h2 className="fs-3 fw-bold text-dark mb-1">Configuración y Personalización de Tienda</h2>
              <p className="text-muted mb-0 small">
                Colores, filtros, módulos, atención por WhatsApp, footer y listas de precios configuradas para <strong>{activeCompanyName}</strong>.
              </p>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2">
              <a
                href={selectedCompanyId ? `/tienda?company=${selectedCompanyId}` : '/tienda'}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
              >
                <i className="bi bi-box-arrow-up-right"></i>
                <span>Ver Tienda Online</span>
              </a>

              <button
                type="button"
                className="btn btn-outline-danger rounded-pill px-3 py-2 fw-semibold"
                onClick={handleResetDefaults}
                title="Restablecer valores de fábrica"
              >
                <i className="bi bi-arrow-counterclockwise me-1"></i> Restablecer
              </button>

              <button
                type="button"
                className="btn btn-primary rounded-pill px-4 py-2 fw-bold d-inline-flex align-items-center gap-2 shadow"
                onClick={handleSaveConfig}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-cloud-check-fill"></i>
                    <span>Guardar Configuración</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 2. Navigation Tabs */}
        <div className="bg-light px-4 border-top">
          <ul className="nav nav-tabs border-0 gap-2 flex-nowrap" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <li className="nav-item">
              <button
                className={`nav-link text-nowrap border-0 py-3 fw-semibold ${activeTab === 'modules' ? 'active bg-white text-primary border-bottom border-primary border-3' : 'text-secondary'}`}
                onClick={(e) => handleTabSelect('modules', e)}
              >
                <i className="bi bi-funnel me-2"></i>
                Filtros del Catálogo
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link text-nowrap border-0 py-3 fw-semibold ${activeTab === 'commercial' ? 'active bg-white text-primary border-bottom border-primary border-3' : 'text-secondary'}`}
                onClick={(e) => handleTabSelect('commercial', e)}
              >
                <i className="bi bi-sliders2-vertical me-2"></i>
                Configuración Comercial
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link text-nowrap border-0 py-3 fw-semibold ${activeTab === 'related' ? 'active bg-white text-primary border-bottom border-primary border-3' : 'text-secondary'}`}
                onClick={(e) => handleTabSelect('related', e)}
              >
                <i className="bi bi-boxes me-2"></i>
                Artículos Relacionados
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* 3. Tab Contents */}

      {/* TAB: FILTROS Y MÓDULOS DE LA TIENDA */}
      {activeTab === 'modules' && (
        <div className="row g-4">
          <div className="col-12 col-xl-8">
            {/* GRUPO 1: FILTROS DEL CATÁLOGO */}
            <div className="card border-0 shadow-sm rounded-4 bg-white mb-4">
              <div className="card-header bg-white py-3 px-4 border-0">
                <div className="d-flex align-items-center gap-2">
                  <div className="p-2 bg-primary-subtle text-primary rounded-3">
                    <i className="bi bi-funnel fs-5"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold text-dark mb-0">Filtros de Búsqueda y Catálogo Lateral</h5>
                    <small className="text-muted">Personalizá qué filtros estarán activos y visibles para los compradores.</small>
                  </div>
                </div>
              </div>
              <div className="card-body p-4 pt-0">
                <div className="d-flex flex-column gap-3">
                  {/* Switch: Marcas */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 bg-white rounded-circle shadow-xs text-primary">
                        <i className="bi bi-award fs-5"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-0">Filtro por Marcas</h6>
                        <small className="text-muted">Permite a los usuarios filtrar artículos por marca registrada.</small>
                      </div>
                    </div>
                    <div className="form-check form-switch fs-4 mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchFilterBrands"
                        checked={config.modules?.filterBrands !== false}
                        onChange={(e) => handleModuleToggle('filterBrands', e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* Switch: Colores */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 bg-white rounded-circle shadow-xs text-danger">
                        <i className="bi bi-palette fs-5"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-0">Filtro por Colores / Variedades</h6>
                        <small className="text-muted">Muestra la paleta interactiva de colores para artículos que tienen variedades.</small>
                      </div>
                    </div>
                    <div className="form-check form-switch fs-4 mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchFilterColors"
                        checked={config.modules?.filterColors !== false}
                        onChange={(e) => handleModuleToggle('filterColors', e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* Switch: Talles */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 bg-white rounded-circle shadow-xs text-info">
                        <i className="bi bi-rulers fs-5"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-0">Filtro por Talles y Medidas</h6>
                        <small className="text-muted">Muestra botones interactivos de talles (S, M, L) o medidas configuradas en rubros.</small>
                      </div>
                    </div>
                    <div className="form-check form-switch fs-4 mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchFilterSizes"
                        checked={config.modules?.filterSizes !== false}
                        onChange={(e) => handleModuleToggle('filterSizes', e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* Switch: Presentación */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 bg-white rounded-circle shadow-xs text-warning">
                        <i className="bi bi-boxes fs-5"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-0">Filtro de Presentación (Bulto / Unidad)</h6>
                        <small className="text-muted">Permite a los clientes alternar entre compra por unidad suelta o bulto cerrado.</small>
                      </div>
                    </div>
                    <div className="form-check form-switch fs-4 mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchFilterPresentation"
                        checked={config.modules?.filterPresentation !== false}
                        onChange={(e) => handleModuleToggle('filterPresentation', e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* Switch: Ofertas */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 bg-white rounded-circle shadow-xs text-danger">
                        <i className="bi bi-tag-fill fs-5"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-0">Filtro de Promociones y Ofertas</h6>
                        <small className="text-muted">Agrega el botón destacado para ver exclusivamente artículos con precio especial o descuento.</small>
                      </div>
                    </div>
                    <div className="form-check form-switch fs-4 mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchFilterOffers"
                        checked={config.modules?.filterOffers !== false}
                        onChange={(e) => handleModuleToggle('filterOffers', e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* Switch: Stock */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 bg-white rounded-circle shadow-xs text-success">
                        <i className="bi bi-check2-circle fs-5"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-0">Filtro de Disponibilidad (Solo con Stock)</h6>
                        <small className="text-muted">Permite a los visitantes filtrar y ver solo productos disponibles.</small>
                      </div>
                    </div>
                    <div className="form-check form-switch fs-4 mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchFilterStock"
                        checked={config.modules?.filterStock !== false}
                        onChange={(e) => handleModuleToggle('filterStock', e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* Switch: Ordenamiento */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 bg-white rounded-circle shadow-xs text-secondary">
                        <i className="bi bi-sort-alpha-down fs-5"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-0">Selector de Ordenamiento</h6>
                        <small className="text-muted">Permite a los clientes ordenar por precio menor/mayor o nombre A-Z.</small>
                      </div>
                    </div>
                    <div className="form-check form-switch fs-4 mb-0">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchFilterSort"
                        checked={config.modules?.filterSort !== false}
                        onChange={(e) => handleModuleToggle('filterSort', e.target.checked)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* COLUMNA DERECHA: RESUMEN Y ESTADO EN VIVO */}
          <div className="col-12 col-xl-4">
            <div className="card border-0 shadow-sm rounded-4 bg-white p-4 sticky-top" style={{ top: '20px' }}>
              <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-funnel text-primary fs-5"></i>
                  <h6 className="fw-bold mb-0 text-dark">Resumen de Filtros</h6>
                </div>
                <span className="badge bg-primary-subtle text-primary rounded-pill px-2.5 py-1 fw-bold">
                  En Vivo
                </span>
              </div>

              <p className="small text-muted mb-3">
                Vista previa de los filtros que estarán activos en la barra lateral del catálogo de <strong>{currentCompanyName}</strong>:
              </p>

              <div className="d-flex flex-column gap-2 mb-4">
                <div className="small fw-bold text-uppercase text-muted" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                  Filtros Activos ({[
                    config.modules?.filterBrands !== false,
                    config.modules?.filterColors !== false,
                    config.modules?.filterSizes !== false,
                    config.modules?.filterPresentation !== false,
                    config.modules?.filterOffers !== false,
                    config.modules?.filterStock !== false,
                    config.modules?.filterSort !== false
                  ].filter(Boolean).length}/7)
                </div>

                <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                  <span 
                    className={`badge rounded-pill ${config.modules?.filterBrands !== false ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-light text-muted border text-decoration-line-through'}`}
                    style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <i className="bi bi-award"></i> Marcas
                  </span>
                  <span 
                    className={`badge rounded-pill ${config.modules?.filterColors !== false ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-light text-muted border text-decoration-line-through'}`}
                    style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <i className="bi bi-palette"></i> Colores
                  </span>
                  <span 
                    className={`badge rounded-pill ${config.modules?.filterSizes !== false ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-light text-muted border text-decoration-line-through'}`}
                    style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <i className="bi bi-rulers"></i> Talles
                  </span>
                  <span 
                    className={`badge rounded-pill ${config.modules?.filterPresentation !== false ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-light text-muted border text-decoration-line-through'}`}
                    style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <i className="bi bi-boxes"></i> Presentación
                  </span>
                  <span 
                    className={`badge rounded-pill ${config.modules?.filterOffers !== false ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-light text-muted border text-decoration-line-through'}`}
                    style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <i className="bi bi-tag-fill"></i> Ofertas
                  </span>
                  <span 
                    className={`badge rounded-pill ${config.modules?.filterStock !== false ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-light text-muted border text-decoration-line-through'}`}
                    style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <i className="bi bi-check2-circle"></i> Stock
                  </span>
                  <span 
                    className={`badge rounded-pill ${config.modules?.filterSort !== false ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-light text-muted border text-decoration-line-through'}`}
                    style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <i className="bi bi-sort-alpha-down"></i> Ordenar
                  </span>
                </div>
              </div>

              <div className="p-3 bg-light rounded-3 border">
                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2" style={{ fontSize: '0.82rem' }}>
                  <i className="bi bi-lightbulb text-warning fs-6"></i>
                  <span>Dinamismo Inteligente</span>
                </h6>
                <p className="small text-muted mb-0 lh-base" style={{ fontSize: '0.75rem' }}>
                  Incluso con un filtro habilitado, si un rubro o catálogo no posee artículos con ese atributo (ej: talles o colores), la tienda lo ocultará automáticamente para no mostrar opciones vacías.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: CONFIGURACIÓN COMERCIAL */}
      {activeTab === 'commercial' && (
        <div className="row g-4">
          <div className="col-12 col-xl-8">
            <div className="card border-0 shadow-sm rounded-4 bg-white mb-4">
              <div className="card-header bg-white py-3 px-4 border-0">
                <h5 className="fw-bold text-dark mb-0">Políticas de Precios y Acceso</h5>
                <small className="text-muted">Definí cómo interactúan los clientes visitantes y clientes registrados.</small>
              </div>
              <div className="card-body p-4">
                <div className="d-flex flex-column gap-4">
                  {/* Visibilidad de precios públicos */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div>
                      <h6 className="fw-bold text-dark mb-1">Mostrar Precios a Visitantes no Autenticados</h6>
                      <p className="small text-muted mb-0">
                        Si está deshabilitado, los visitantes verán el botón "Iniciar Sesión para ver Precios" y no podrán ver montos sin cuenta.
                      </p>
                    </div>
                    <div className="form-check form-switch fs-4">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchPublicPrices"
                        checked={config.commercial?.publicPricesEnabled || false}
                        onChange={(e) => handleCommercialChange('publicPricesEnabled', e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* Estado de Empresa y Sitio Web en SGES000 */}
                  <div className="p-3 rounded-3 bg-light border">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <div className="flex-grow-1">
                        <h6 className="fw-bold text-dark mb-1">
                          <i className="bi bi-building me-2 text-primary"></i>Empresa Asociada a la Tienda
                        </h6>
                        <p className="small text-muted mb-0">
                          La tienda y su dominio se gestionan automáticamente desde la empresa activa y los parámetros de la tabla <code>sges000</code> (<code>l_sitien</code> y <code>d_sitien</code>).
                        </p>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="badge bg-white text-dark border px-3 py-2 rounded-pill fw-bold">
                          {activeCompanyName} (#{selectedCompanyId})
                        </span>
                        {activeCompany?.l_sitien ? (
                          <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2 rounded-pill fw-semibold">
                            <i className="bi bi-check-circle-fill me-1"></i>Habilitada {activeCompany.d_sitien ? `(${activeCompany.d_sitien})` : ''}
                          </span>
                        ) : (
                          <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle px-3 py-2 rounded-pill fw-semibold">
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>Deshabilitada (l_sitien = No)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Selector de Lista de Precios por Defecto */}
                  <div className="p-3 rounded-3 bg-light border">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <div className="flex-grow-1">
                        <h6 className="fw-bold text-dark mb-1">
                          <i className="bi bi-tag-fill me-2 text-primary"></i>Lista de Precios Mostrada en la Tienda
                        </h6>
                        <p className="small text-muted mb-0">
                          Seleccioná qué lista de precios de <strong>{activeCompanyName}</strong> se exhibirá a los visitantes y clientes en la tienda online.
                        </p>
                      </div>
                      <div style={{ minWidth: '240px' }}>
                        <select
                          className="form-select fw-semibold"
                          id="selectDefaultPriceList"
                          value={config.commercial?.defaultPriceList || 1}
                          onChange={(e) => handleCommercialChange('defaultPriceList', parseInt(e.target.value, 10) || 1)}
                        >
                          {priceLists.length > 0 ? (
                            priceLists.map((pl) => (
                              <option key={pl.n_lista} value={pl.n_lista}>
                                Lista {pl.n_lista} {pl.d_obser ? `- ${pl.d_obser}` : ''} {pl.l_prede ? '(Predeterminada en sistema)' : ''}
                              </option>
                            ))
                          ) : (
                            <>
                              <option value={1}>Lista 1 - Minorista / Contado</option>
                              <option value={2}>Lista 2 - Mayorista</option>
                              <option value={3}>Lista 3 - Distribuidor</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Generar automáticamente Nota de Pedido de tienda (NPT) en el sistema */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div>
                      <h6 className="fw-bold text-dark mb-1">Generar automáticamente Nota de Pedido de tienda (NPT) en el sistema</h6>
                      <p className="small text-muted mb-0">
                        Si está activado, cada pedido realizado por los clientes en la tienda online creará automáticamente un comprobante de tipo NPT en el sistema.
                      </p>
                    </div>
                    <div className="form-check form-switch fs-4">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchSaveOrderAsQuote"
                        checked={config.commercial?.saveOrderAsQuote || false}
                        onChange={(e) => handleCommercialChange('saveOrderAsQuote', e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* Configuración de WhatsApp para Recepción de Pedidos */}
                  <div className="p-3 rounded-3 bg-light border">
                    <h6 className="fw-bold text-dark mb-1">
                      <i className="bi bi-whatsapp me-2 text-success"></i>Recepción de Pedidos por WhatsApp
                    </h6>
                    <p className="small text-muted mb-3">
                      Número al que los clientes enviarán su pedido al finalizar la compra en el carrito de la tienda.
                    </p>
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-bold text-secondary">Teléfono WhatsApp de Pedidos</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white"><i className="bi bi-telephone text-success"></i></span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Ej: 5491142476622"
                            value={config.commercial?.whatsappOrderNumber || ''}
                            onChange={(e) => handleCommercialChange('whatsappOrderNumber', e.target.value)}
                          />
                        </div>
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>Código de país + código de área sin 0 ni 15.</small>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-bold text-secondary">Encabezado de Mensaje del Pedido</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="¡Hola! Quiero confirmar mi pedido..."
                          value={config.commercial?.orderMessageTemplate || ''}
                          onChange={(e) => handleCommercialChange('orderMessageTemplate', e.target.value)}
                        />
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>Texto introductorio antes del detalle de productos.</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-4">
            <div className="card border-0 shadow-sm rounded-4 bg-white p-4 sticky-top" style={{ top: '90px' }}>
              <h5 className="fw-bold text-dark mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-info-circle text-primary"></i>
                Resumen Comercial
              </h5>
              <p className="small text-muted mb-3">
                Configuración de ventas activa para la tienda online:
              </p>

              <div className="rounded-3 border overflow-hidden">
                <ul className="list-group list-group-flush small mb-0">
                  <li className="list-group-item px-3 py-2.5 d-flex justify-content-between align-items-center">
                    <span className="text-muted">Empresa activa:</span>
                    <span className="fw-bold text-dark text-end ms-2">{activeCompanyName}</span>
                  </li>
                  <li className="list-group-item px-3 py-2.5 d-flex justify-content-between align-items-center">
                    <span className="text-muted">Precios públicos:</span>
                    <span className={`badge ${config.commercial?.publicPricesEnabled ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                      {config.commercial?.publicPricesEnabled ? 'Visibles' : 'Solo usuarios'}
                    </span>
                  </li>
                  <li className="list-group-item px-3 py-2.5 d-flex justify-content-between align-items-center">
                    <span className="text-muted">Lista predeterminada:</span>
                    <span className="fw-bold text-primary">Lista {config.commercial?.defaultPriceList || 1}</span>
                  </li>
                  <li className="list-group-item px-3 py-2.5 d-flex justify-content-between align-items-center">
                    <span className="text-muted">Generar NPT:</span>
                    <span className={`badge ${config.commercial?.saveOrderAsQuote ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                      {config.commercial?.saveOrderAsQuote ? 'Activado' : 'Desactivado'}
                    </span>
                  </li>
                  <li className="list-group-item px-3 py-2.5 d-flex justify-content-between align-items-center border-0">
                    <span className="text-muted">WhatsApp Pedidos:</span>
                    <span className="fw-semibold text-dark text-end ms-2">{config.commercial?.whatsappOrderNumber || 'No asignado'}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: ARTÍCULOS RELACIONADOS */}
      {activeTab === 'related' && (
        <div className="row g-4">
          <div className="col-12 col-xl-8">
            {/* CARD 1: ACTIVACIÓN Y CRITERIO */}
            <div className="card border-0 shadow-sm rounded-4 bg-white mb-4">
              <div className="card-header bg-white py-3 px-4 border-0">
                <div className="d-flex align-items-center gap-2">
                  <div className="p-2 bg-primary-subtle text-primary rounded-3">
                    <i className="bi bi-boxes fs-5"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold text-dark mb-0">Sección de Artículos Relacionados</h5>
                    <small className="text-muted">
                      Configurá los artículos recomendados que se exhiben dentro del modal de detalle de producto.
                    </small>
                  </div>
                </div>
              </div>
              <div className="card-body p-4 pt-0">
                <div className="d-flex flex-column gap-4">

                  {/* Switch General de Activación */}
                  <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border">
                    <div>
                      <h6 className="fw-bold text-dark mb-1">
                        Mostrar Artículos Relacionados en el Detalle
                      </h6>
                      <p className="small text-muted mb-0">
                        Si está habilitado, los compradores verán una tira de artículos sugeridos al abrir el detalle de cualquier producto.
                      </p>
                    </div>
                    <div className="form-check form-switch fs-4">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        role="switch"
                        id="switchRelatedEnabled"
                        checked={config.relatedProducts?.enabled !== false}
                        onChange={(e) => handleRelatedChange('enabled', e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* Selector de Criterio */}
                  <div className="p-3 rounded-3 bg-light border">
                    <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <i className="bi bi-diagram-3-fill text-primary"></i>
                      Criterio de Recomendación
                    </h6>
                    <p className="small text-muted mb-3">
                      Elegí la regla con la que el catálogo seleccionará automáticamente los artículos sugeridos.
                    </p>

                    <div className="row g-3">
                      {/* Opción 1: Subfamilia y/o Rubro */}
                      <div className="col-12 col-md-6">
                        <div
                          className={`card h-100 p-3 rounded-3 cursor-pointer transition-all border-2 ${
                            (config.relatedProducts?.criteria || 'subfamily_or_rubro') === 'subfamily_or_rubro'
                              ? 'border-primary bg-primary-subtle bg-opacity-25 shadow-xs'
                              : 'border bg-white hover-border-secondary'
                          }`}
                          onClick={() => handleRelatedChange('criteria', 'subfamily_or_rubro')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-start gap-2">
                            <input
                              type="radio"
                              className="form-check-input mt-1"
                              name="relatedCriteria"
                              id="crit_sub_rubro"
                              checked={(config.relatedProducts?.criteria || 'subfamily_or_rubro') === 'subfamily_or_rubro'}
                              onChange={() => handleRelatedChange('criteria', 'subfamily_or_rubro')}
                            />
                            <div>
                              <label htmlFor="crit_sub_rubro" className="fw-bold text-dark mb-1 cursor-pointer d-block">
                                Misma Subfamilia y/o Rubro
                                <span className="badge bg-primary text-white ms-2 px-2 py-0.5" style={{ fontSize: '0.68rem' }}>Recomendado</span>
                              </label>
                              <p className="small text-muted mb-0 lh-base" style={{ fontSize: '0.8rem' }}>
                                Prioriza artículos de la misma subfamilia y completa los cupos con otros productos del mismo rubro general.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Opción 2: Mismo Rubro */}
                      <div className="col-12 col-md-6">
                        <div
                          className={`card h-100 p-3 rounded-3 cursor-pointer transition-all border-2 ${
                            config.relatedProducts?.criteria === 'same_rubro'
                              ? 'border-primary bg-primary-subtle bg-opacity-25 shadow-xs'
                              : 'border bg-white hover-border-secondary'
                          }`}
                          onClick={() => handleRelatedChange('criteria', 'same_rubro')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-start gap-2">
                            <input
                              type="radio"
                              className="form-check-input mt-1"
                              name="relatedCriteria"
                              id="crit_same_rubro"
                              checked={config.relatedProducts?.criteria === 'same_rubro'}
                              onChange={() => handleRelatedChange('criteria', 'same_rubro')}
                            />
                            <div>
                              <label htmlFor="crit_same_rubro" className="fw-bold text-dark mb-1 cursor-pointer d-block">
                                Mismo Rubro únicamente
                              </label>
                              <p className="small text-muted mb-0 lh-base" style={{ fontSize: '0.8rem' }}>
                                Muestra únicamente artículos que compartan el mismo rubro comercial, sin distinguir subfamilias.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Opción 3: Misma Subfamilia */}
                      <div className="col-12 col-md-6">
                        <div
                          className={`card h-100 p-3 rounded-3 cursor-pointer transition-all border-2 ${
                            config.relatedProducts?.criteria === 'same_subfamily'
                              ? 'border-primary bg-primary-subtle bg-opacity-25 shadow-xs'
                              : 'border bg-white hover-border-secondary'
                          }`}
                          onClick={() => handleRelatedChange('criteria', 'same_subfamily')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-start gap-2">
                            <input
                              type="radio"
                              className="form-check-input mt-1"
                              name="relatedCriteria"
                              id="crit_same_subfamily"
                              checked={config.relatedProducts?.criteria === 'same_subfamily'}
                              onChange={() => handleRelatedChange('criteria', 'same_subfamily')}
                            />
                            <div>
                              <label htmlFor="crit_same_subfamily" className="fw-bold text-dark mb-1 cursor-pointer d-block">
                                Misma Subfamilia únicamente
                              </label>
                              <p className="small text-muted mb-0 lh-base" style={{ fontSize: '0.8rem' }}>
                                Filtra estrictamente productos que compartan la misma subfamilia específica.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Opción 4: Personalizados */}
                      <div className="col-12 col-md-6">
                        <div
                          className={`card h-100 p-3 rounded-3 cursor-pointer transition-all border-2 ${
                            config.relatedProducts?.criteria === 'custom'
                              ? 'border-primary bg-primary-subtle bg-opacity-25 shadow-xs'
                              : 'border bg-white hover-border-secondary'
                          }`}
                          onClick={() => handleRelatedChange('criteria', 'custom')}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-start gap-2">
                            <input
                              type="radio"
                              className="form-check-input mt-1"
                              name="relatedCriteria"
                              id="crit_custom"
                              checked={config.relatedProducts?.criteria === 'custom'}
                              onChange={() => handleRelatedChange('criteria', 'custom')}
                            />
                            <div>
                              <label htmlFor="crit_custom" className="fw-bold text-dark mb-1 cursor-pointer d-block">
                                Personalizados / Manuales
                              </label>
                              <p className="small text-muted mb-0 lh-base" style={{ fontSize: '0.8rem' }}>
                                Elegí manualmente una selección de artículos destacados para sugerir en los detalles.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Configuración de Textos y Cantidad */}
                  <div className="p-3 rounded-3 bg-light border">
                    <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <i className="bi bi-fonts text-primary"></i>
                      Textos y Cantidad a Mostrar
                    </h6>
                    <p className="small text-muted mb-3">
                      Personalizá los títulos del bloque y el límite máximo de productos visibles.
                    </p>

                    <div className="row g-3">
                      <div className="col-12 col-md-5">
                        <label className="form-label small fw-bold text-secondary">Título de la Sección</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Ej: Artículos Relacionados"
                          value={config.relatedProducts?.title || ''}
                          onChange={(e) => handleRelatedChange('title', e.target.value)}
                        />
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>Por defecto: "Artículos Relacionados".</small>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label small fw-bold text-secondary">Subtítulo o Bajada</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Ej: Productos similares que también te pueden interesar"
                          value={config.relatedProducts?.subtitle || ''}
                          onChange={(e) => handleRelatedChange('subtitle', e.target.value)}
                        />
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>Texto secundario de orientación.</small>
                      </div>
                      <div className="col-12 col-md-3">
                        <label className="form-label small fw-bold text-secondary">Máximo de Artículos</label>
                        <select
                          className="form-select fw-semibold"
                          value={config.relatedProducts?.maxItems || 6}
                          onChange={(e) => handleRelatedChange('maxItems', parseInt(e.target.value, 10) || 6)}
                        >
                          <option value={4}>4 artículos</option>
                          <option value={6}>6 artículos (recomendado)</option>
                          <option value={8}>8 artículos</option>
                          <option value={12}>12 artículos</option>
                        </select>
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>Cupo máximo mostrado.</small>
                      </div>
                    </div>
                  </div>

                  {/* Bloque selector si criterio === 'custom' */}
                  {config.relatedProducts?.criteria === 'custom' && (
                    <div className="p-3 rounded-3 bg-light border">
                      <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                        <i className="bi bi-search text-primary"></i>
                        Selección de Artículos Personalizados
                      </h6>
                      <p className="small text-muted mb-3">
                        Buscá artículos por código o nombre y agregalos a la lista de sugerencias.
                      </p>

                      {/* Buscador */}
                      <div className="input-group mb-2">
                        <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Escribí código o descripción del artículo (ej: 1001, Caja, Bolsas)..."
                          value={searchArticuloQuery}
                          onChange={(e) => handleSearchArticulos(e.target.value)}
                        />
                        {searchArticuloQuery && (
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => { setSearchArticuloQuery(''); setSearchedArticulos([]); }}
                          >
                            Limpiar
                          </button>
                        )}
                      </div>

                      {/* Spinner */}
                      {searchingArticulos && (
                        <div className="text-center py-2 text-muted small">
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Buscando en catálogo...
                        </div>
                      )}

                      {/* Resultados de búsqueda */}
                      {searchedArticulos.length > 0 && (
                        <div className="list-group mb-3 shadow-sm border rounded-3 overflow-hidden" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                          {searchedArticulos.map(art => (
                            <div key={art.c_artic || art.id} className="list-group-item list-group-item-action d-flex align-items-center justify-content-between p-2">
                              <div className="d-flex align-items-center gap-2">
                                <ProductThumbnail product={art} size={36} />
                                <div>
                                  <div className="fw-bold text-dark small">{art.d_artic}</div>
                                  <small className="text-muted font-monospace">{art.c_artic} {art.d_rubro ? `| ${art.d_rubro}` : ''}</small>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary rounded-pill px-3 fw-semibold"
                                onClick={() => handleAddCustomArticle(art)}
                              >
                                <i className="bi bi-plus-lg me-1"></i> Agregar
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Lista de Artículos Seleccionados */}
                      <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="small fw-bold text-secondary text-uppercase">
                            Artículos Agregados ({config.relatedProducts?.customArticles?.length || 0})
                          </span>
                          {config.relatedProducts?.customArticles?.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0 small text-decoration-none"
                              onClick={() => handleRelatedChange('customArticles', [])}
                            >
                              Vaciar lista
                            </button>
                          )}
                        </div>

                        {(!config.relatedProducts?.customArticles || config.relatedProducts.customArticles.length === 0) ? (
                          <div className="text-center p-3 bg-white rounded-3 border text-muted small">
                            <i className="bi bi-info-circle me-1"></i> Aún no agregaste artículos personalizados. Utilizá el buscador para sumar artículos.
                          </div>
                        ) : (
                          <div className="row g-2">
                            {config.relatedProducts.customArticles.map((art, idx) => {
                              const code = art.c_artic || art;
                              const name = art.d_artic || code;
                              return (
                                <div key={code || idx} className="col-12 col-md-6">
                                  <div className="p-2 bg-white rounded-3 border d-flex align-items-center justify-content-between shadow-xs">
                                    <div className="d-flex align-items-center gap-2 text-truncate me-2">
                                      <ProductThumbnail product={art} size={32} />
                                      <div className="text-truncate">
                                        <span className="d-block text-truncate fw-semibold text-dark small">{name}</span>
                                        <span className="text-muted font-monospace" style={{ fontSize: '0.72rem' }}>{code}</span>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger border-0 p-1 rounded-circle"
                                      title="Quitar de la lista"
                                      onClick={() => handleRemoveCustomArticle(code)}
                                    >
                                      <i className="bi bi-x-lg"></i>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>

          {/* COLUMNA DERECHA: VISTA PREVIA Y EXPLICACIÓN */}
          <div className="col-12 col-xl-4">
            <div className="card border-0 shadow-sm rounded-4 bg-white p-4 sticky-top" style={{ top: '90px' }}>
              <h5 className="fw-bold text-dark mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-eye text-primary"></i>
                Vista Previa & Funcionamiento
              </h5>
              <p className="small text-muted mb-3">
                Así funcionará la recomendación en el modal de detalle del cliente:
              </p>

              <div className="p-3 rounded-3 border bg-light mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="badge bg-primary-subtle text-primary fw-bold">
                    {config.relatedProducts?.enabled !== false ? 'Activo' : 'Desactivado'}
                  </span>
                  <span className="badge bg-secondary-subtle text-secondary">
                    Hasta {config.relatedProducts?.maxItems || 6} items
                  </span>
                </div>
                <h6 className="fw-bold text-dark mb-1">
                  {config.relatedProducts?.title || 'Artículos Relacionados'}
                </h6>
                <small className="text-muted d-block mb-3">
                  {config.relatedProducts?.subtitle || 'Productos similares que también te pueden interesar'}
                </small>

                <div className="p-3 bg-white rounded-3 border">
                  <div className="small fw-semibold text-dark d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-gear-wide-connected text-primary fs-6"></i>
                    <span>Regla Activa:</span>
                  </div>
                  <div className="badge bg-light text-dark border text-wrap lh-base px-3 py-2 text-start w-100" style={{ fontSize: '0.8rem', fontWeight: 500 }}>
                    {config.relatedProducts?.criteria === 'same_rubro' && 'Mismo Rubro del artículo en pantalla'}
                    {config.relatedProducts?.criteria === 'same_subfamily' && 'Misma Subfamilia del artículo en pantalla'}
                    {config.relatedProducts?.criteria === 'custom' && `Personalizados (${config.relatedProducts?.customArticles?.length || 0} artículos elegidos)`}
                    {(!config.relatedProducts?.criteria || config.relatedProducts?.criteria === 'subfamily_or_rubro') && 'Misma Subfamilia (prioritario) + Mismo Rubro (completar cupo)'}
                  </div>
                </div>
              </div>

              <div className="p-3 bg-light rounded-3 border">
                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2" style={{ fontSize: '0.82rem' }}>
                  <i className="bi bi-lightbulb text-warning fs-6"></i>
                  <span>Navegación Fluida</span>
                </h6>
                <p className="small text-muted mb-0 lh-base" style={{ fontSize: '0.75rem' }}>
                  Al hacer clic en cualquiera de los artículos relacionados mostrados dentro del modal, la vista se actualiza inmediatamente mostrando el nuevo producto seleccionado, sin necesidad de cerrar y reabrir el detalle.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botón flotante permanente para Guardar Configuración de Tienda */}
      <div
        className="position-fixed d-flex align-items-center gap-2"
        style={{
          bottom: '28px',
          right: '32px',
          zIndex: 1060
        }}
      >
        <button
          type="button"
          className="btn btn-primary btn-lg rounded-pill px-4 py-3 shadow-lg d-flex align-items-center gap-2 fw-bold text-white border-2 border-white"
          onClick={handleSaveConfig}
          disabled={saving}
          style={{
            boxShadow: '0 8px 25px rgba(13, 110, 253, 0.45)',
            transition: 'all 0.2s ease-in-out',
            fontSize: '1.05rem',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
          onMouseEnter={(e) => {
            if (!saving) {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0px) scale(1)';
          }}
          title="Guardar todos los cambios de la tienda online"
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span>Guardando Tienda...</span>
            </>
          ) : (
            <>
              <i className="bi bi-cloud-check-fill fs-5"></i>
              <span>Guardar Configuración</span>
            </>
          )}
        </button>
      </div>

    </div>
  );
};

export default EcommerceAdminSettings;
